import { apiFetch } from '@/api/client'

/**
 * The two-level catalogue of product interests a lead is tagged with.
 *
 * There is no delete, by design: leads reference these rows rather than copying them, so removing
 * one would take the meaning out of every lead pointing at it. Retiring is `active: false` — gone
 * from the picker, still readable on old leads — and a typo is fixed by renaming, which reaches
 * those leads too.
 */
export type ProductInterest = {
  id: string
  name: string
  /** Position within the category. The order is a business decision, not alphabetical. */
  sortIndex: number
  active: boolean
  categoryId: string
}

export type ProductCategory = {
  id: string
  name: string
  sortIndex: number
  active: boolean
  interests: ProductInterest[]
}

export type ProductCatalog = {
  categories: ProductCategory[]
}

export type CategoryInput = {
  name: string
  /** Omitted appends to the end, which is what adding to a list is expected to do. */
  sortIndex?: number
  active: boolean
}

/** `categoryId` doubles as the move: sending a different one relocates the interest. */
export type InterestInput = {
  categoryId: string
  name: string
  sortIndex?: number
  active: boolean
}

/**
 * The whole catalogue in one request. Not paged and not split per category on purpose — the picker
 * needs all of it, and a request per category is the round trip that stalls on a fairground
 * network.
 */
export function loadProductCatalog(
  includeInactive = false,
  signal?: AbortSignal,
): Promise<ProductCatalog> {
  const parameters = new URLSearchParams()
  if (includeInactive) {
    parameters.set('includeInactive', 'true')
  }
  return apiFetch<ProductCatalog>(`/api/product-categories?${parameters}`, { signal })
}

export function createCategory(body: CategoryInput): Promise<ProductCategory> {
  return apiFetch<ProductCategory>('/api/product-categories', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function updateCategory(id: string, body: CategoryInput): Promise<ProductCategory> {
  return apiFetch<ProductCategory>(`/api/product-categories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export function createInterest(body: InterestInput): Promise<ProductInterest> {
  return apiFetch<ProductInterest>('/api/product-interests', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export function updateInterest(id: string, body: InterestInput): Promise<ProductInterest> {
  return apiFetch<ProductInterest>(`/api/product-interests/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}
